import { useState, useEffect } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CustomTextField from '../common/CustomTextField';
import SelectModelInput from '../models/SelectModelInput';
import SelectPromptTemplateInput from '../prompt-templates/SelectPromptTemplateInput';
import { useCallLLMMutation } from '../../redux/services/llmApi';
import { useGetModelsQuery } from '../../redux/services/modelApi';

const handleTextInputChange = setTextInput => (e) => {
  setTextInput(e.target.value);
};

const handlePromptTemplateChange = setPromptTemplateId => (e, value) => {
  setPromptTemplateId(value);
};

const handleJsonInputChange = ({setJsonInput, setJsonError}) => (e) => {
  const value = e.target.value;
  setJsonInput(value);
  // Validate JSON if not empty
  if (value.trim()) {
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (err) {
      setJsonError('Invalid JSON format');
    }
  } else {
    setJsonError(null);
  }
};

const handleSubmit = ({
  jsonInput, 
  setJsonError, 
  selectedModel, 
  modelId, 
  promptTemplateId, 
  textInput, 
  callLLM, 
  setResult
}) => async (e) => {
    e.preventDefault();
    
    // Validate JSON if provided
    let parsedJson = null;
    if (jsonInput.trim()) {
      try {
        parsedJson = JSON.parse(jsonInput);
      } catch (err) {
        setJsonError('Invalid JSON format');
        return;
      }
    }

    // Build request body
    const body = {
      model_id: modelId,
      prompt_template_id: promptTemplateId,
      text_input: textInput,
      json_input: parsedJson ? JSON.stringify(parsedJson) : null,
    };

    // Remove undefined/null fields
    Object.keys(body).forEach(key => {
      if (body[key] === null || body[key] === undefined || body[key] === '') {
        delete body[key];
      }
    });

    const result = await callLLM(body);
    
    if (result.error) {
      setResult(null);
    } else if (result.data) {
      // Handle both direct response and nested data structure
      const textOutput = result.data.text_output || result.data.data?.text_output || '';
      setResult(textOutput);
    }
  };

const LLMCallScreen = props => {
  const [modelId, setModelId] = useState(1);
  const [selectedModel, setSelectedModel] = useState(1);
  const [promptTemplateId, setPromptTemplateId] = useState(1);
  const [textInput, setTextInput] = useState('');
  const [jsonInput, setJsonInput] = useState('');
  const [result, setResult] = useState(null);
  const [jsonError, setJsonError] = useState(null);
  
  const [callLLM, response] = useCallLLMMutation();
  const { data: modelsData } = useGetModelsQuery({});

  // Update selectedModel when modelId changes
  useEffect(() => {
    if (modelId && modelsData?.data) {
      const model = modelsData.data.find(m => m.id === modelId || m.name === modelId);
      if (model) {
        setSelectedModel(model);
      }
    } else if (!modelId) {
      setSelectedModel(null);
    }
  }, [modelId, modelsData]);

  return (
    <Paper>
      <Stack spacing={3} padding={4}>
        <Typography variant='h1' align='center'>
          LLM Call Interface
        </Typography>
        
        {response?.error && (
          <Alert severity='error'>
            Error calling LLM: {response.error?.data?.message || response.error?.message || 'Unknown error'}
          </Alert>
        )}
        
        {jsonError && (
          <Alert severity='error'>
            {jsonError}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Main chat area - left side on desktop, top on mobile */}
          <Grid item xs={12} md={9} order={{ xs: 1, md: 1 }}>
            <Stack spacing={3} p={2}>
              {/* Messages/Response area */}
              {response?.data && (
                <Stack spacing={2}>
                  <Typography variant='h2'>
                    Response:
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#f3f3f3',
                      padding: 2,
                      borderRadius: 1,
                      minHeight: '100px',
                      maxHeight: '400px',
                      overflow: 'auto'
                    }}
                  >
                    <Typography variant='body'>
                      {response.data.output_text || 'No output returned.'}
                    </Typography>
                  </Box>
                </Stack>
              )}

              {/* Chat input form */}
              <form onSubmit={handleSubmit({
                jsonInput,
                setJsonError,
                selectedModel,
                modelId,
                promptTemplateId,
                textInput,
                callLLM,
                setResult
              })}>
                <Stack spacing={2}>
                  <CustomTextField
                    label='Text Input'
                    multiline
                    rows={6}
                    fullWidth
                    value={textInput}
                    onChange={handleTextInputChange(setTextInput)}
                    helperText='Enter the text to send to the LLM'
                  />

                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                    disabled={response?.isLoading || !!jsonError}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {response?.isLoading ? 'Submitting...' : 'Submit'}
                  </Button>
                </Stack>
              </form>
            </Stack>
          </Grid>

          {/* Settings panel - right side on desktop, bottom on mobile */}
          <Grid 
            item 
            xs={12} 
            md={3} 
            order={{ xs: 2, md: 2 }}
            sx={{
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1
            }}
          >
            <Stack spacing={3}>
              <SelectPromptTemplateInput
                label='Prompt Template'
                onChange={handlePromptTemplateChange}
                value={promptTemplateId}
              />
              <SelectModelInput
                label='Model'
                onChange={(e, value) => {
                  setModelId(value);
                }}
                value={modelId}
              />
              <CustomTextField
                label='JSON Input (Optional)'
                multiline
                rows={5}
                fullWidth
                value={jsonInput}
                onChange={handleJsonInputChange({setJsonInput, setJsonError})}
                helperText='Enter additional JSON data (optional)'
                error={!!jsonError}
              />
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
};

export default LLMCallScreen;

